import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(null); // 'manager' | 'field' | null

  useEffect(() => {
    loadStoredAuth();
  }, []);

  // Initialize viewMode when user loads
  useEffect(() => {
    if (user) {
      if (user.role === 'supervisor') {
        const stored = localStorage.getItem('viewMode');
        setViewMode(stored === 'field' ? 'field' : 'manager');
      } else {
        setViewMode(null);
      }
    }
  }, [user]);

  const loadStoredAuth = async () => {
    try {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        api.setToken(storedToken);
      }
    } catch (error) {
      console.error('Error loading auth:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.login(email, password);
    const { access_token, user: userData } = response;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    api.setToken(access_token);
    setToken(access_token);
    setUser(userData);
  };

  const register = async (data) => {
    const response = await api.register(data);
    const { access_token, user: userData } = response;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    api.setToken(access_token);
    setToken(access_token);
    setUser(userData);
  };

  const logout = async () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('viewMode');
    api.setToken(null);
    setToken(null);
    setUser(null);
    setViewMode(null);
  };

  const toggleViewMode = () => {
    const newMode = viewMode === 'manager' ? 'field' : 'manager';
    setViewMode(newMode);
    localStorage.setItem('viewMode', newMode);
    return newMode;
  };

  // Supervisors in field mode act like guards for routing purposes
  const effectiveRole = (user?.role === 'supervisor' && viewMode === 'field') ? 'guard' : user?.role;

  return (
    <AuthContext.Provider value={{ 
      user, token, loading, login, register, logout,
      viewMode, toggleViewMode, effectiveRole
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
